import React from 'react';
import { OutlineSlideCard } from '@/components/outline/OutlineSlideCard';
import { cn } from '@/lib/utils';
import { GsapSlidePreviewEnhanced, type GsapSlidePreviewRef } from '@/components/slides/GsapSlidePreviewEnhanced';
import type { VideoManifest } from '@/types/videoManifest';
import { VideoCompositionPlayer, type VideoCompositionPlayerRef } from './VideoCompositionPlayer';
import { StudioBuildFrameB } from './StudioBuildFrameB';

type CardProps = React.ComponentProps<typeof OutlineSlideCard>;
type PlayerProps = React.ComponentProps<typeof VideoCompositionPlayer>;
type PreviewProps = React.ComponentProps<typeof GsapSlidePreviewEnhanced>;

interface StudioCanvasProps {
  plannedSlide: CardProps['slide'] | null | undefined;
  plannedIndex: number;
  plannedTotal: number;
  layouts: CardProps['layouts'];
  generationStatus: CardProps['generationStatus'];
  generatingThinking: CardProps['generatingThinking'];
  showComposition: boolean;
  compositionRef: React.Ref<VideoCompositionPlayerRef>;
  manifest: VideoManifest | null;
  conversationId: string;
  currentSlideId: string | null;
  onCompositionStateChange: PlayerProps['onStateChange'];
  onManifestUpdate: PlayerProps['onManifestUpdate'];
  onSlideStart: PlayerProps['onSlideStart'];
  onVideoEnd: PlayerProps['onVideoEnd'];
  onCompositionError: PlayerProps['onError'];
  slideData: PreviewProps['slide'] | null;
  previewKey: string;
  previewRef: React.Ref<GsapSlidePreviewRef>;
  previewConversationId: PreviewProps['conversationId'];
  onPlayerStateChange: PreviewProps['onPlayerStateChange'];
  onEditToolbarStateChange: PreviewProps['onEditToolbarStateChange'];
  onEditModeChange: PreviewProps['onEditModeChange'];
  onSaveComplete: PreviewProps['onSaveComplete'];
  resolution: { width: number; height: number };
  /** `b`: Direction B's stage (beta only): the stage tone, and the player framed to the video so it draws no bars. */
  variant?: 'b';
  /** Direction B: the scene on stage is not built yet; its plan or draft fills the frame, over the player when there is one. */
  buildFrame?: React.ComponentProps<typeof StudioBuildFrameB>;
  /** Direction B: the still over the paused player at the start of a scene (its stored preview). */
  poster?: string | null;
  /** Direction B: the video goes edge to edge. */
  fullscreen?: boolean;
  /** Direction B: the player is about to move into a scene, and has (re)loaded its manifest. */
  onTransitionStarted?: (toSlideId: string) => void;
  onManifestLoaded?: () => void;
  onSlideReloaded?: (slideId: string) => void;
}

/** The stage of the Studio tab: a scene's plan until it is first built, else the whole video, else the one scene. */
export const StudioCanvas: React.FC<StudioCanvasProps> = ({
  plannedSlide,
  plannedIndex,
  plannedTotal,
  layouts,
  generationStatus,
  generatingThinking,
  showComposition,
  compositionRef,
  manifest,
  conversationId,
  currentSlideId,
  onCompositionStateChange,
  onManifestUpdate,
  onSlideStart,
  onVideoEnd,
  onCompositionError,
  slideData,
  previewKey,
  previewRef,
  previewConversationId,
  onPlayerStateChange,
  onEditToolbarStateChange,
  onEditModeChange,
  onSaveComplete,
  resolution,
  variant,
  buildFrame,
  poster,
  fullscreen = false,
  onTransitionStarted,
  onManifestLoaded,
  onSlideReloaded,
}) => {
  const b = variant === 'b';
  // The frame takes the video's own shape, as large as the stage allows, so the player has no letterbox to draw.
  const frame = (children: React.ReactNode) => (
    <div
      data-video-frame
      className={cn('aspect-[var(--b-aspect)] w-[min(100cqw,calc(100cqh*var(--b-aspect-num)))] overflow-hidden', !fullscreen && 'rounded-[var(--b-radius-video)] shadow-[var(--b-shadow-video)]')}
      style={{ '--b-aspect': `${resolution.width} / ${resolution.height}`, '--b-aspect-num': resolution.width / resolution.height } as React.CSSProperties}
    >
      {children}
    </div>
  );
  const player = manifest && (
    <VideoCompositionPlayer
      ref={compositionRef}
      manifest={manifest}
      conversationId={conversationId}
      initialSlideId={currentSlideId || undefined}
      onStateChange={onCompositionStateChange}
      onManifestUpdate={onManifestUpdate}
      onSlideStart={onSlideStart}
      onVideoEnd={onVideoEnd}
      onError={onCompositionError}
      onTransitionStarted={onTransitionStarted}
      onManifestLoaded={onManifestLoaded}
      onSlideReloaded={onSlideReloaded}
      className={b ? 'h-full w-full' : 'flex-1 min-h-0'}
    />
  );

  const content = (
    /* PRIORITY 1: Show OutlineSlideCard ONLY for slides that have NEVER been generated
        Once a slide is generated, always show preview (thinking shown in chat/filmstrip/outline) */
    b && showComposition && player ? (
      // One place for the player whether or not a scene's plan shows over it, so it is never reloaded: not when
      // playback reaches a scene still building, and not when that scene lands.
      frame(
        <div className="relative h-full w-full">
          {player}
          {poster && !buildFrame && <img data-stage-poster src={poster} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />}
          {buildFrame && (
            <div className="absolute inset-0">
              <StudioBuildFrameB {...buildFrame} />
            </div>
          )}
        </div>,
      )
    ) : b && buildFrame ? (
      frame(<StudioBuildFrameB {...buildFrame} />)
    ) : plannedSlide ? (
      <div className="flex-1 min-h-0 flex items-center justify-center p-8 overflow-auto">
        <div className="w-full max-w-2xl">
          <OutlineSlideCard
            slide={plannedSlide}
            index={plannedIndex}
            totalSlides={plannedTotal}
            layouts={layouts}
            onUpdate={() => {}}
            onOpenLayoutSelector={() => {}}
            isReadOnly={true}
            defaultExpanded={true}
            generationStatus={generationStatus}
            generatingThinking={generatingThinking}
          />
        </div>
      </div>
    ) : showComposition && player ? (
      /* COMPOSITION MODE: Show VideoCompositionPlayer for seamless playback */
      player
    ) : slideData ? (
      /* SINGLE SLIDE MODE: Show GsapSlidePreviewEnhanced for editing */
      <GsapSlidePreviewEnhanced
        key={previewKey}
        ref={previewRef}
        slide={slideData}
        conversationId={previewConversationId}
        outlineSlideId={currentSlideId ?? undefined}
        renderControls={false}
        fullscreenKey={b ? false : undefined}
        onPlayerStateChange={onPlayerStateChange}
        onEditToolbarStateChange={onEditToolbarStateChange}
        onEditModeChange={onEditModeChange}
        onSaveComplete={onSaveComplete}
        resolution={resolution}
        className={b ? 'flex-1 min-h-0 self-stretch' : 'flex-1 min-h-0'}
      />
    ) : (
      <div className="flex-1 min-h-0 flex items-center justify-center">
        <div className="studio-text-secondary text-sm">No slide selected</div>
      </div>
    )
  );

  if (b) {
    return (
      <div data-stage="b" data-video-stage className={cn('flex min-h-0 flex-1 flex-col', fullscreen ? 'bg-black' : 'bg-b-ground p-6 min-[1800px]:p-8')}>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center [container-type:size]">{content}</div>
      </div>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col min-h-0 p-4 studio-canvas"
      data-video-stage
    >
      {content}
    </div>
  );
};
